<template>
  <q-card class="inhdialog-container">
    <div class="inhdialog-header">
      <h1 class="dialog_name">
        {{ `${ $t('full_info_counterparty') } "${infoContainer.Name || ''}"` }}
      </h1>
      <q-btn
        flat
        round
        icon="close"
        size="md"
        color="accent"
        :title="$t('close_label')"
        @click="close"
      />
    </div>

    <div class="dialog_content">
      <!-- Пока данные загружаются -->
      <div v-if="dataLoading" class="loading-container q-mt-sm">
        <div v-for="n in 6" :key="n" class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <!-- Жирный заголовок (короткая широкая полоска) -->
            <div class="skeleton skeleton-title"></div>

            <!-- Текстовый спан (длинная полоска) -->
            <div
              class="skeleton skeleton-text"
              :class="{
                'skeleton-text-short': n % 3 === 0,
                'skeleton-text-medium': n % 3 === 1,
              }"
            ></div>
          </div>
        </div>
      </div>

      <div v-else class="inner_container">
        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name mr-xs">{{ $t('creation_date')}}:</div>
            <span>{{ dateFormatted(infoContainer.Date) }}</span>
            <span v-if="infoContainer.CreatedInCrm">,&nbsp;{{ $t('created_in_one_c') }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('inn') }}:</div>
            <span>{{ infoContainer.TaxpayerNumber }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('kpp') }}:</div>
            <span>{{ infoContainer.ReasonCode }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('personal_account2') }}:</div>
            <span>{{ infoContainer.PersonalAccount }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('bank_or_settlement_account') }}:</div>
            <span>{{ infoContainer.BankAccount }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('bank_name') }}:</div>
            <span>{{ infoContainer.BankName }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('bik') }}:</div>
            <span>{{ infoContainer.BankIdCode }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('bank_correspondent_account') }}:</div>
            <span>{{ infoContainer.CorrespondentBankAccount }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('legal_address') }}:</div>
            <span>{{ infoContainer.LegalAddress }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('legal_form') }}:</div>
            <span>{{ infoContainer.LegalForm }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('comment') }}:</div>
            <span>{{ infoContainer.Note }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('linked_to_client') }}:</div>
            <q-btn flat dense round 
            icon="edit"
            color="accent" 
            class="q-ml-xs" size="11px" 
            :title="$t('edit')" @click="showCounterpartyChangeDialog = true" />
          </div>
          <div class="file-list">
            {{ infoContainer.Client?.Name }}
          </div>
        </div>
        <div class="field-group">
          <div class="bold-name flex items-center">{{ $t('incoming_invoices') }}</div>
          <div class="file-list">
            <connections-table-html
              v-if="tmp_connections_inc.length"
              :model-value="tmp_connections_inc"
              :check-sums="false"
              :show-sums="false"
              :hide-remove-btn="true"
            />
          </div>
        </div>
        <div class="field-group">
          <div class="bold-name flex items-center">{{ $t('outgoing_invoices') }}</div>
          <div class="file-list">
            <connections-table-html
              v-if="tmp_connections_out.length"
              :model-value="tmp_connections_out"
              :check-sums="false"
              :show-sums="false"
              :hide-remove-btn="true"
            />
          </div>
        </div>
      </div>
    </div>

    <q-dialog v-model="showCounterpartyChangeDialog" no-shake :full-width="true">
      <counterparty-change-dialog
        :counterparty-id="counterpartyId"
        :current-client-name="infoContainer.Name || ''"
        @close="showCounterpartyChangeDialog = false"
        @done="handleCounterpartyChangeDone"
      />
    </q-dialog>
  </q-card>
</template>

<script lang="ts">
import { Vue, Component, Emit, Prop } from "vue-facing-decorator";
import { date, QBtn, QIcon, QSelect } from "quasar";
import { financesStore } from "../../../../store/finances";
import { reactive } from "vue";
import { CounterpartyInfo } from "../../../../models/interfaces/finances/counterparty-models";
import { BillRowProps, ConnectionLine } from "../../../../store/heartbeat-workers/useEntityStore";
import { BillLinkedTypeDescriptor, BillType } from "../../../../models/etc/types";
import { BillLinkedBase, BillWithManyLinks, BillWithOneLink } from "../../../../models/interfaces/finances/finance-models";
import ConnectionsTableHtml from "../ConnectionsTableHtml.vue";

@Component({
  components: {
    QBtn,
    QIcon,
    QSelect,
    ConnectionsTableHtml
  },
})
export default class CounterpartyInfoComponent extends Vue {
  @Prop({ type: Number, default: 0 }) readonly counterpartyId!: number;

  // stores import
  finStore: any | undefined = undefined;
  infoContainer: CounterpartyInfo = reactive({} as CounterpartyInfo);
  dataLoading: boolean = false;
  tmp_connections_inc: ConnectionLine[] = reactive([]);
  tmp_connections_out: ConnectionLine[] = reactive([]);
  showCounterpartyChangeDialog: boolean = false;

  created() {
    this.finStore = financesStore();
  }

  mounted() {
    this.loadCPartyInfo(this.counterpartyId);
  }

  dateFormatted(val: string): string {
    return date.formatDate(new Date(val), "DD.MM.YY");
  }

  async loadCPartyInfo(billNum: number) {
    this.dataLoading = true;
    this.infoContainer = (await this.finStore.getInfoCounterparty(
      billNum
    )) as CounterpartyInfo;

    // @ts-ignore
    if (this.infoContainer.Bills) {
      this.tmp_connections_inc = reactive(
        this.infoContainer.Bills.filter(b => b.Type === BillType.Incoming).map((conn: BillLinkedBase) => {
          if (conn.Descriptor === BillLinkedTypeDescriptor.BillWithOneLink) {
            const c = conn as BillWithOneLink;
            return {
              conn_id: c.Id,
              percent: c.PayedPercent,
              amount: c.TotalAmount,
              debit: c.PayedAmount,
              movement: undefined,
              billRowType: BillRowProps.BillLinkedBase,
              bill: c,
              canEditAmounts: false,
              canEditDebits: false,
              canBeDeleted: false,
            } as ConnectionLine;
          } else if (conn.Descriptor === BillLinkedTypeDescriptor.BillWithManyLinks) {
            const c = conn as BillWithManyLinks;
            return {
              conn_id: c.Id,
              percent: c.LinkPercent,
              amount: c.LinkAmount,
              debit: c.PayedAmount,
              billRowType: BillRowProps.BillLinkedBase,
              bill: c,
              canEditAmounts: false,
              canEditDebits: false,
              canBeDeleted: false,
            } as ConnectionLine;
          } else {
            return {} as ConnectionLine
          }
        })
      );

      this.tmp_connections_out = reactive(
        this.infoContainer.Bills.filter(b => b.Type === BillType.Outgoing).map((conn: BillLinkedBase) => {
          if (conn.Descriptor === BillLinkedTypeDescriptor.BillWithOneLink) {
            const c = conn as BillWithOneLink;
            return {
              conn_id: c.Id,
              percent: c.PayedPercent,
              amount: c.TotalAmount,
              debit: c.PayedAmount,
              movement: undefined,
              billRowType: BillRowProps.BillLinkedBase,
              bill: c,
              canEditAmounts: false,
              canEditDebits: false,
              canBeDeleted: false,
            } as ConnectionLine;
          } else if (conn.Descriptor === BillLinkedTypeDescriptor.BillWithManyLinks) {
            const c = conn as BillWithManyLinks;
            return {
              conn_id: c.Id,
              percent: c.LinkPercent,
              amount: c.LinkAmount,
              debit: c.PayedAmount,
              billRowType: BillRowProps.BillLinkedBase,
              bill: c,
              canEditAmounts: false,
              canEditDebits: false,
              canBeDeleted: false,
            } as ConnectionLine;
          } else {
            return {} as ConnectionLine
          }
        })
      );
    }
    
    this.dataLoading = false;
  }

  async handleCounterpartyChangeDone() {
    this.showCounterpartyChangeDialog = false;
    await this.loadCPartyInfo(this.counterpartyId);
  }

  @Emit("close")
  close() {}
}
</script>

<style lang="scss" scoped></style>
